import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

type TooltipPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

interface TooltipProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  placement?: TooltipPlacement;
  enterDelay?: number;
  enterTouchDelay?: number;
  leaveDelay?: number;
  arrow?: boolean;
  open?: boolean;
  disableInteractive?: boolean;
  disableHoverListener?: boolean;
  disableTouchListener?: boolean;
  disableFocusListener?: boolean;
  className?: string;
  /**
   * DOM node the tooltip content is portalled into. Defaults to
   * `document.body`. Pass a specific ancestor (e.g. the hover-expanding
   * navbar) when the content must stay inside that element's `:hover`
   * subtree so it doesn't break a CSS-hover-driven layout.
   */
  container?: HTMLElement | null;
  PopperProps?: Record<string, unknown>;
  slotProps?: Record<string, unknown>;
  componentsProps?: Record<string, unknown>;
}

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'ora-tooltip z-[5000] max-w-xs rounded-md border border-border-subtle bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-popover',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));

TooltipContent.displayName = 'TooltipContent';

const resolvePlacement = (placement: TooltipPlacement = 'bottom') => {
  const [side, align] = placement.split('-') as [
    'top' | 'bottom' | 'left' | 'right',
    'start' | 'end' | undefined,
  ];

  return {
    side,
    align: align ?? 'center',
  } as const;
};

const wrapChild = (children: React.ReactNode) => {
  if (React.isValidElement(children)) {
    return children;
  }

  return <span>{children}</span>;
};

const Tooltip: React.FC<TooltipProps> = ({
  title,
  children,
  placement = 'bottom',
  enterDelay = 0,
  enterTouchDelay,
  leaveDelay,
  arrow = false,
  open,
  disableInteractive = false,
  disableHoverListener = false,
  disableTouchListener = false,
  disableFocusListener = false,
  className,
  container,
}) => {
  const hasContent = !(
    title === undefined ||
    title === null ||
    title === '' ||
    title === false
  );

  if (!hasContent) {
    return <>{children}</>;
  }

  const { side, align } = resolvePlacement(placement);

  const delayDuration =
    typeof enterTouchDelay === 'number' ? enterTouchDelay : enterDelay;

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root
        open={open}
        disableHoverableContent={disableInteractive}
      >
        <TooltipPrimitive.Trigger
          asChild
          onPointerMove={
            disableHoverListener ? (event) => event.preventDefault() : undefined
          }
          onTouchStart={
            disableTouchListener ? (event) => event.preventDefault() : undefined
          }
          onFocus={
            disableFocusListener ? (event) => event.preventDefault() : undefined
          }
        >
          {wrapChild(children)}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={container ?? undefined}>
          <TooltipPrimitive.Content
            side={side}
            align={align}
            sideOffset={8}
            className={cn(
              'ora-tooltip z-[5000] max-w-xs rounded-md border border-border-subtle bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-popover',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
              className
            )}
          >
            {title}
            {arrow && (
              <TooltipPrimitive.Arrow className="fill-popover stroke-border-subtle" />
            )}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
      {typeof leaveDelay === 'number' ? (
        <span className="sr-only" data-tooltip-leave-delay={leaveDelay} />
      ) : null}
    </TooltipPrimitive.Provider>
  );
};

export default Tooltip;
export { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent };
